import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Advent of Code Days 17 & 18 – Spinlocks and Interpreters",
  "description": "",
  "published": "2017-12-18T00:00:00.000Z",
  "redirect_from": ["/blog/advent-code-days-17-18-spinlocks-interpreters/swizec/7977"],
  "categories": "Haskell, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "3Eee0va5t0A",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Days 17 and 18 both defeated me. Star 1 was easy, Star 2 was not. Both were pretty quick to solve for the first star, though, so let's look at that.`}</p>
    <h2 {...{
      "id": "day-17--spinlock"
    }}>{`Day 17 – Spinlock`}</h2>
    <p>{`For Advent of Code Day 17, we had to implement a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Spinlock"
      }}>{`spinlock`}</a>{`. Spinlocks are a way to implement busy waiting 👇`}</p>
    <blockquote>
      <p parentName="blockquote">{`In software engineering, a spinlock is a lock which causes a thread trying to acquire it to simply wait in a loop ("spin") while repeatedly checking if the lock is available. Since the thread remains active but is not performing a useful task, the use of such a lock is a kind of busy waiting.`}</p>
    </blockquote>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/carbon-spinlock.png",
        "alt": "A naive spinlock implementation in Haskell"
      }}></img></p>
    <p>{`There's no video for Day 17 because I was doing it in bed and yes, I did fall asleep while waiting for Star 2 to finish computing. It never did; my algorithm was too slow.`}</p>
    <p>{`Unlike a real spinlock, the puzzle spinlock is trying to eat up infinite memory as well as infinite time.`}</p>
    <blockquote>
      <p parentName="blockquote">{`For example, if the spinlock were to step 3 times per insert, the circular buffer would begin to evolve like this (using parentheses to mark the current position after each iteration of the algorithm):`}</p>
      <p parentName="blockquote">{`(0), the initial state before any insertions.`}</p>
      <p parentName="blockquote">{`0 (1): the spinlock steps forward three times (0, 0, 0), and then inserts the first value, 1, after it. 1 becomes the current position.`}</p>
      <p parentName="blockquote">{`0 (2) 1: the spinlock steps forward three times (0, 1, 0), and then inserts the second value, 2, after it. 2 becomes the current position.`}</p>
      <p parentName="blockquote">{`0 2 (3) 1: the spinlock steps forward three times (1, 0, 2), and then inserts the third value, 3, after it. 3 becomes the current position.`}</p>
    </blockquote>
    <p>{`The question was `}<em parentName="p">{`"What is the value right after 2017 gets inserted into the buffer?"`}</em>{`.`}</p>
    <p>{`To find out, I built a recursive implementation of the spinlock algorithm above in Haskell. Because Haskell is fun.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`spinlock::[Int] -> Int -> Int -> Int -> Int -> (Int, [Int])
spinlock buffer steps pos i iterations
    | i < iterations = spinlock (left ++ [i] ++ right) steps nextPos (i+1) iterations
    | otherwise = (nextPos, buffer)
    where spinPos = mod (pos+steps) (length buffer)
          (left, right) = splitAt (spinPos+1) buffer
          nextPos = spinPos+1
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`spinlock`}</inlineCode>{` method takes 5 arguments, which I'm sure is sacrilege in Haskell, and returns a tuple: An integer and a list of integers.`}</p>
    <p>{`Arguments look like this:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`buffer`}</inlineCode>{` is the current state of our circular buffer`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`steps`}</inlineCode>{` tells us how many steps we do on each spin`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`pos`}</inlineCode>{` gives us the current position in our buffer`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`i`}</inlineCode>{` says how many times we've iterated`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`iterations`}</inlineCode>{` tells us how many times to iterate in total`}</li>
    </ul>
    <p>{`The algorithm itself was simple to implement, but fraught with `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Off-by-one_error"
      }}>{`off-by-one errors`}</a>{`.`}</p>
    <p>{`If we have to keep going – `}<inlineCode parentName="p">{`i < iterations`}</inlineCode>{` – then recurse with an edited buffer, updated position and `}<inlineCode parentName="p">{`i+1`}</inlineCode>{`. Otherwise, return the result. A tuple with the next position and final buffer.`}</p>
    <p>{`We get the position after spinning, `}<inlineCode parentName="p">{`spinPos`}</inlineCode>{`, as a remainder between current position `}<inlineCode parentName="p">{`pos`}</inlineCode>{` and `}<inlineCode parentName="p">{`steps`}</inlineCode>{`, and the buffer length. Split the buffer into `}<inlineCode parentName="p">{`left`}</inlineCode>{` and `}<inlineCode parentName="p">{`right`}</inlineCode>{` at position after the spin, and say the next position is going to be there too.`}</p>
    <p>{`This worked great for the `}<inlineCode parentName="p">{`2017`}</inlineCode>{` iterations from Star 1.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`star1::Int -> Int
star1 steps = buffer!!pos
    where (pos, buffer) = spinlock [0] steps 0 1 2017
`}</code></pre>
    <p>{`A little slow maybe, but it worked.`}</p>
    <p>{`For Star 2, they wanted us to find the value after `}<inlineCode parentName="p">{`0`}</inlineCode>{` when 50,000,000 iterations are performed. This did not go so well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`star2::Int -> Int
star2 steps = buffer!!(zeroAt+1)
    where (pos, buffer) = spinlock [0] steps 0 1 50000000
          zeroAt = Data.Maybe.fromJust $ elemIndex 0 buffer
`}</code></pre>
    <p>{`The idea is simple: Iterate 50 million times, look for the `}<inlineCode parentName="p">{`0`}</inlineCode>{`, return the value after it.`}</p>
    <p>{`But the spinlock never finishes. Haskell's `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Lazy_evaluation"
      }}>{`lazy evaluation`}</a>{` gets in the way, and I couldn't figure out how to make it stop.`}</p>
    <p>{`With lazy evaluation, we keep all iterations of the spinlock in memory until we print the final result. That's a problem.`}</p>
    <p><em parentName="p">{`shrug`}</em></p>
    <h2 {...{
      "id": "day-18--a-programming-language-interpreter"
    }}>{`Day 18 – A programming language interpreter`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/carbon-interpreter.png",
        "alt": "The gist of a simple interpreter built in JavaScript"
      }}></img></p>
    <p>{`On Day 18 of our Advent of Code, we had to build an interpreter for a simple programming language. There are 7 commands that take 1 or 2 arguments. Arguments can be registers or values.`}</p>
    <blockquote>
      <p parentName="blockquote"><inlineCode parentName="p">{`snd X`}</inlineCode>{` plays a sound with a frequency equal to the value of X.`}</p>
      <p parentName="blockquote"><inlineCode parentName="p">{`set X Y`}</inlineCode>{` sets register X to the value of Y.`}</p>
      <p parentName="blockquote"><inlineCode parentName="p">{`add X Y`}</inlineCode>{` increases register X by the value of Y.`}</p>
      <p parentName="blockquote"><inlineCode parentName="p">{`mul X Y`}</inlineCode>{` sets register X to the result of multiplying the value contained in register X by the value of Y.`}</p>
      <p parentName="blockquote"><inlineCode parentName="p">{`mod X Y`}</inlineCode>{` sets register X to the remainder of dividing the value contained in register X by the value of Y (that is, it sets X to the result of X modulo Y).`}</p>
      <p parentName="blockquote"><inlineCode parentName="p">{`rcv X`}</inlineCode>{` recovers the frequency of the last sound played, but only when the value of X is not zero. (If it is zero, the command does nothing.)`}</p>
      <p parentName="blockquote"><inlineCode parentName="p">{`jgz X Y`}</inlineCode>{` jumps with an offset of the value of Y, but only if the value of X is greater than zero. (An offset of 2 skips the next instruction, an offset of -1 jumps to the previous instruction, and so on.)`}</p>
    </blockquote>
    <p>{`Our goal is to find the first non-zero value that `}<inlineCode parentName="p">{`rcv`}</inlineCode>{` finds.`}</p>
    <p>{`I built this one in JavaScript because why not. 😇`}</p>
    <p>{`We start with a bunch of `}<inlineCode parentName="p">{`registers`}</inlineCode>{`, which are a JavaScript `}<inlineCode parentName="p">{`Map`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function initRegisters() {
  const registers = new Map(
    "abcdefghijklmnopqrstuvwxyz".split("").map((l) => [l, 0])
  );
  registers.set("sound", null);
  registers.set("pointer", 0);

  return registers;
}
`}</code></pre>
    <p>{`This creates a register for each letter of the alphabet plus a `}<inlineCode parentName="p">{`sound`}</inlineCode>{` register and a `}<inlineCode parentName="p">{`pointer`}</inlineCode>{`. `}<inlineCode parentName="p">{`sound`}</inlineCode>{` will be where `}<inlineCode parentName="p">{`snd`}</inlineCode>{` puts its values and `}<inlineCode parentName="p">{`rcv`}</inlineCode>{` reads them from, `}<inlineCode parentName="p">{`pointer`}</inlineCode>{` is going to point to the current line of code we're executing.`}</p>
    <p>{`The interpreter itself comes as just 39 lines of code. It's a simple language after all. Although I do think it's got enough instructions to be `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Turing_completeness"
      }}>{`Turing-complete`}</a>{`, but it lacks the memory. 25 registers won't cut it for Turing completeness.`}</p>
    <p>{`You could, of course, expand it to have infinite registers 🤔`}</p>
    <p>{`Anyway, the interpreter 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function execute(registers, command) {
  const [com, val1, val2] = command.trim().split(" ");

  function getVal(val) {
    if (registers.has(val)) {
      return registers.get(val);
    } else {
      return Number(val);
    }
  }

  let jumped = false,
    kill = false;

  const commands = {
    snd: (a) => registers.set("sound", getVal(a)),
    set: (a, b) => registers.set(a, getVal(b)),
    add: (a, b) => registers.set(a, getVal(a) + getVal(b)),
    mul: (a, b) => registers.set(a, getVal(a) * getVal(b)),
    mod: (a, b) => registers.set(a, getVal(a) % getVal(b)),
    rcv: (a) => (
      console.log("SOUND:", getVal("sound")),
      (kill = true),
      registers.set(a, getVal("sound"))
    ),
    jgz: (a, b) =>
      getVal(a) > 0
        ? ((jumped = true),
          registers.set("pointer", getVal("pointer") + getVal(b)))
        : null,
  };

  commands[com](val1, val2);

  if (!jumped) {
    registers.set("pointer", getVal("pointer") + 1);
  }

  return [kill, registers];
}
`}</code></pre>
    <p>{`We split the line of code into a `}<inlineCode parentName="p">{`command`}</inlineCode>{` and two values, `}<inlineCode parentName="p">{`val1`}</inlineCode>{` and `}<inlineCode parentName="p">{`val2`}</inlineCode>{`.`}</p>
    <p>{`Then we define a function for reading values, `}<inlineCode parentName="p">{`getVal`}</inlineCode>{`. If the value given is a known register, we read from it; otherwise, we return the value itself.`}</p>
    <p>{`After that, we need two flags: `}<inlineCode parentName="p">{`jump`}</inlineCode>{` tells us if we executed a jump command, and `}<inlineCode parentName="p">{`kill`}</inlineCode>{` tells us if we have to stop executing.`}</p>
    <p>{`A dictionary mapping all possible commands to a function that executes them helps us run the commands. Each function manipulates the `}<inlineCode parentName="p">{`registers`}</inlineCode>{` and potentially flips the `}<inlineCode parentName="p">{`jump`}</inlineCode>{` and `}<inlineCode parentName="p">{`kill`}</inlineCode>{` flags.`}</p>
    <p>{`When the current line of code is executed, we advance our `}<inlineCode parentName="p">{`pointer`}</inlineCode>{` by `}<inlineCode parentName="p">{`+1`}</inlineCode>{` if we didn't jump. The interpreter returns the `}<inlineCode parentName="p">{`kill`}</inlineCode>{` flag and the new `}<inlineCode parentName="p">{`registers`}</inlineCode>{`.`}</p>
    <p>{`The registers are actually changed in place, and there's no need to return, but I think this approach makes our implementation clearer.`}</p>
    <p>{`With the interpreter in hand, we then have to add some looping to find the answer to AoC 18 Star 1.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function star1() {
  let registers = initRegisters(),
    kill = false;
  const program = input.split("\\n").filter((command) => command.length > 0);

  // find sound value at first non-zero rcv
  while (
    registers.get("pointer") >= 0 &&
    registers.get("pointer") < program.length &&
    !kill
  ) {
    [kill, registers] = execute(registers, program[registers.get("pointer")]);
  }
}
`}</code></pre>
    <p>{`Create registers, split program into lines, `}<inlineCode parentName="p">{`execute`}</inlineCode>{` until a `}<inlineCode parentName="p">{`while`}</inlineCode>{` condition is met. Either we jumped out of the program, or a line set the `}<inlineCode parentName="p">{`kill`}</inlineCode>{` flag.`}</p>
    <p>{`Works like a charm 👌`}</p>
    <p><strong parentName="p">{`Star 2`}</strong>{` is where it gets tricky. Those `}<inlineCode parentName="p">{`snd`}</inlineCode>{` and `}<inlineCode parentName="p">{`rcv`}</inlineCode>{` commands weren't actually about sound; they were `}<inlineCode parentName="p">{`send`}</inlineCode>{` and `}<inlineCode parentName="p">{`receive`}</inlineCode>{` commands, and you're meant to run two copies of this code in parallel.`}</p>
    <p>{`They communicate with `}<inlineCode parentName="p">{`snd`}</inlineCode>{` and `}<inlineCode parentName="p">{`rcv`}</inlineCode>{`.`}</p>
    <p>{`We have to expand our `}<inlineCode parentName="p">{`sound`}</inlineCode>{` register into a message queue and add some logic for how it's shared between the two programs. Additionally, the puzzle wants us to pause execution of each program while it waits for the queue to get values.`}</p>
    <p><img parentName="p" {...{
        "src": "/7ecb6c6b7b52b704e663fde62e65d36d/media-3oEdvc4JCV2HJa7UQ0-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Sounds hard. So I went to bed.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      